import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableRow } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { LogEntry } from "../../service/LogService";
import { formatTime, LevelChip, orangeButton, outlinedButton } from "./logStyles";

interface Props {
  entry: LogEntry | null;
  onClose: () => void;
  onFollowRequest: (requestId: string) => void;
}

const json = (value: unknown) => (value === undefined ? "" : JSON.stringify(value, null, 2));

/** Every field of one log entry */
export default function LogDetailDialog({ entry, onClose, onFollowRequest }: Props) {
  const [t] = useTranslation("global");
  if (!entry) return null;

  const rows: [string, React.ReactNode][] = [
    [t("logs.time"), formatTime(entry.createdAt)],
    [t("logs.level"), <LevelChip level={entry.level} />],
    [t("logs.category"), entry.category],
    [t("logs.event"), entry.event],
    [t("logs.message"), entry.message],
    [t("logs.requestId"), entry.requestId],
    [t("logs.instance"), entry.instance],
    [t("logs.actor"), entry.actor ? `${entry.actor.role || ""} ${entry.actor.userId || ""}` : ""],
    ["Tenant", entry.tenantId],
    [t("logs.duration"), entry.durationMs !== undefined ? `${entry.durationMs} ms` : ""],
    [t("logs.http"), entry.http ? `${entry.http.method || ""} ${entry.http.path || ""} ${entry.http.status || ""} (${entry.http.ip || ""})` : ""],
    [t("logs.ids"), entry.ids ? <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{json(entry.ids)}</pre> : ""],
    [t("logs.meta"), entry.meta !== undefined ? <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{json(entry.meta)}</pre> : ""],
  ];

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("logs.detailTitle")}</DialogTitle>
      <DialogContent>
        <Table size="small">
          <TableBody>
            {rows
              .filter(([, value]) => value !== undefined && value !== "")
              .map(([label, value]) => (
                <TableRow key={label}>
                  <TableCell sx={{ fontWeight: "bold", width: 140, verticalAlign: "top" }}>{label}</TableCell>
                  <TableCell sx={{ wordBreak: "break-word" }}>{value}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        {entry.requestId && (
          <Button variant="contained" onClick={() => onFollowRequest(entry.requestId as string)} sx={orangeButton}>
            {t("logs.followRequest")}
          </Button>
        )}
        <Button variant="outlined" onClick={onClose} sx={outlinedButton}>
          {t("logs.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
